import React, { useState, useEffect } from 'react';
import { useNudges } from '../hooks/useNudges';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Grid,
  Button, Alert, Skeleton, Dialog,
  Badge
} from '@mui/material';
import {
  FlightTakeoff, PhotoLibrary, People, Explore,
  AddCircleOutline,
  PersonAdd, NotificationsActive, TravelExplore,
  AirplanemodeActive,
  ConnectWithoutContact, WorkspacePremium
} from '@mui/icons-material';

import PlanUpgradeModal from '../components/PlanUpgradeModal';
import UserDemoFlow from '../components/demo/UserDemoFlow';
import { UserStep_WhyPercoro as WhyPercoroHero } from './DemoPage';
import { usePlan } from '../hooks/usePlan';
import { PwaInstallButton } from '../components/PwaInstallPrompt';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
const API_URL = process.env.REACT_APP_API_URL || 'https://3yslsbk5n1.execute-api.us-east-1.amazonaws.com/dev';

interface UserProfile {
  display_name?: string;
  first_name?: string;
  email: string;
  created_at?: string;
  createdAt?: string;
}

interface PendingFriend {
  id: string;
  name: string;
  email: string;
}

const ACTION_CARDS = [
  { title: 'My Trips', subtitle: 'View and manage your trips', icon: <TravelExplore sx={{ fontSize: 28 }} />, color: '#C8DDD8', iconColor: '#2D6E6A', path: '/trips' },
  { title: 'Import a Trip', subtitle: 'Upload your booking PDF', icon: <FlightTakeoff sx={{ fontSize: 28 }} />, color: '#F0E0D0', iconColor: '#e53935', path: '/trips/new' },
  { title: 'Trip Companions', subtitle: 'Pro feature', icon: <ConnectWithoutContact sx={{ fontSize: 28 }} />, color: '#D0D8E8', iconColor: '#3949ab', path: '/plans', proFeature: true },
  { title: 'Friends', subtitle: 'Connect and share', icon: <People sx={{ fontSize: 28 }} />, color: '#D8D0E8', iconColor: '#8e24aa', path: '/friends' },
  { title: 'My Photos', subtitle: 'Your travel gallery', icon: <PhotoLibrary sx={{ fontSize: 28 }} />, color: '#EDE0C8', iconColor: '#f57c00', path: '/photos' },
  { title: 'Community', subtitle: 'Explore trips from others', icon: <Explore sx={{ fontSize: 28 }} />, color: '#C8DCE8', iconColor: '#0288d1', path: '/community' },
];


const isNewUser = (createdAt?: string): boolean => {
  if (!createdAt) return true;
  const diffDays = (Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24);
  return diffDays <= 7;
};

const HomePage: React.FC = () => {
  const { plan } = usePlan();
  const { isEnabled, isLoading: flagsLoading } = useFeatureFlags();
  const pwaInstallEnabled = !flagsLoading && isEnabled('pwa_install') === true;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromInvite = searchParams.get('fromInvite') === '1';
  const reconciledCount = parseInt(searchParams.get('reconciled') || '0', 10);
  const { nudges, admin_broadcasts, lifecycle_notifications } = useNudges();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [companionInfoOpen, setCompanionInfoOpen] = useState(false);
  const [pendingFriends, setPendingFriends] = useState<PendingFriend[]>([]);
  const [whyOpen, setWhyOpen] = useState(true);
  const [whyInit, setWhyInit] = useState(false);
  const [showInlineTour, setShowInlineTour] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newUser, setNewUser] = useState(false);

  const totalNotifications = admin_broadcasts.length + lifecycle_notifications.length + nudges.length;
  const friendNudges = nudges.filter(n => n.category === 'friends');

  // Why Percoro hero: expanded by default for new users, collapsed for returning.
  useEffect(() => {
    if (!whyInit && profile) {
      setWhyOpen(newUser);
      setWhyInit(true);
    }
  }, [newUser, profile, whyInit]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('idToken');
        if (!token) { navigate('/login'); return; }
        const res = await fetch(`${API_URL}/users/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) { navigate('/login'); return; }
        const data = await res.json();
        const p: UserProfile = data.profile || data.user || data;
        setProfile(p);
        setNewUser(isNewUser(p.createdAt || p.created_at));
        const friendsRes = await fetch(`${API_URL}/friends/list`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (friendsRes.ok) {
          const friendsData = await friendsRes.json();
          const pending = (friendsData.friends || [])
            .filter((f: any) => f.status === 'pending_received')
            .map((f: any) => ({ id: f.id, name: f.name || f.email, email: f.email }));
          setPendingFriends(pending);
        }
      } catch {
        // still render
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const firstName = profile?.first_name ||
    profile?.display_name?.split(' ')[0] ||
    profile?.email?.split('@')[0] || 'there';

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="text" width={200} height={32} sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          {[1,2,3,4].map(i => <Grid item xs={6} key={i}><Skeleton variant="rounded" height={100} /></Grid>)}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFB', pb: 6 }}>

      {/* Friend invite success banner */}
      {fromInvite && reconciledCount > 0 && (
        <Alert severity="success" icon={<PersonAdd />} sx={{ borderRadius: 0 }}
          action={<Button color="inherit" size="small" onClick={() => navigate('/friends')}>View Friends</Button>}>
          You're now connected! Visit Friends to see your new connection.
        </Alert>
      )}

      {/* Welcome header */}
      <Box sx={{ background: 'linear-gradient(135deg, #2D6E6A 0%, #007a6e 100%)', px: 3, py: 4, color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif', mb: 0.5 }}>
              {newUser ? `Welcome, ${firstName}!` : `Welcome back, ${firstName}.`}
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: '0.95rem', lineHeight: 1.5 }}>
              {newUser
                ? 'Percoro is where your trips come together — plan, document, and share with the people you travel with.'
                : "Here's what's happening with your trips and friends."}
            </Typography>
            {newUser && (
              <Button variant="contained" startIcon={<AddCircleOutline />} onClick={() => navigate('/trips/new')}
                sx={{ mt: 2, bgcolor: 'white', color: '#2D6E6A', fontWeight: 600, borderRadius: '24px',
                  textTransform: 'none', px: 3, '&:hover': { bgcolor: '#f0faf9' } }}>
                Plan your first trip
              </Button>
            )}
          </Box>
          {/* Notification bell — returning users */}
          {!newUser && (
            <Box onClick={() => navigate('/notifications')} sx={{ cursor: 'pointer', textAlign: 'center', ml: 2 }}>
              <Badge badgeContent={totalNotifications} color="error" max={9}
                sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem', fontWeight: 700 } }}>
                <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '50%', width: 52, height: 52,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '2px solid rgba(255,255,255,0.5)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }, transition: 'all 0.2s' }}>
                  <NotificationsActive sx={{ color: 'white', fontSize: 26 }} />
                </Box>
              </Badge>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                {totalNotifications > 0 ? `${totalNotifications} new` : 'All clear'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>


      <Box sx={{ px: 2, py: 3, maxWidth: 800, mx: 'auto' }}>

        {/* === RETURNING USER DASHBOARD === */}
        {!newUser && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>
                Your dashboard
              </Typography>

            </Box>

            {/* Summary count cards */}
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Card onClick={() => navigate('/friends?tab=invites')}
                  sx={{ borderRadius: '16px', cursor: 'pointer', boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                    '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.12)', transform: 'translateY(-2px)' },
                    transition: 'all 0.2s', border: pendingFriends.length > 0 ? '1px solid #F0E0D0' : '1px solid #f0f0f0' }}>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Box sx={{ bgcolor: 'white', borderRadius: '50%', width: 40, height: 40,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1,
                      border: '2px solid #e53935', boxShadow: '0 0 0 3px #F0E0D0' }}>
                      <PersonAdd sx={{ color: '#e53935', fontSize: 18 }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: pendingFriends.length > 0 ? '#e53935' : '#bdbdbd' }}>
                      {pendingFriends.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>
                      Friend invites
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card onClick={() => navigate('/notifications')}
                  sx={{ borderRadius: '16px', cursor: 'pointer', boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                    '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.12)', transform: 'translateY(-2px)' },
                    transition: 'all 0.2s', border: admin_broadcasts.length > 0 ? '1px solid #C8DDD8' : '1px solid #f0f0f0' }}>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Box sx={{ bgcolor: 'white', borderRadius: '50%', width: 40, height: 40,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1,
                      border: '2px solid #2D6E6A', boxShadow: '0 0 0 3px #C8DDD8' }}>
                      <NotificationsActive sx={{ color: '#2D6E6A', fontSize: 18 }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: admin_broadcasts.length > 0 ? '#2D6E6A' : '#bdbdbd' }}>
                      {admin_broadcasts.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>
                      Announcements
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card onClick={() => navigate('/trips')}
                  sx={{ borderRadius: '16px', cursor: 'pointer', boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                    '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.12)', transform: 'translateY(-2px)' },
                    transition: 'all 0.2s', border: lifecycle_notifications.length > 0 ? '1px solid #EDE0C8' : '1px solid #f0f0f0' }}>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Box sx={{ bgcolor: 'white', borderRadius: '50%', width: 40, height: 40,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1,
                      border: '2px solid #f57c00', boxShadow: '0 0 0 3px #EDE0C8' }}>
                      <AirplanemodeActive sx={{ color: '#f57c00', fontSize: 18 }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: lifecycle_notifications.length > 0 ? '#f57c00' : '#bdbdbd' }}>
                      {lifecycle_notifications.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>
                      Trip reminders
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card onClick={() => navigate('/friends')}
                  sx={{ borderRadius: '16px', cursor: 'pointer', boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                    '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.12)', transform: 'translateY(-2px)' },
                    transition: 'all 0.2s', border: friendNudges.length > 0 ? '1px solid #D8D0E8' : '1px solid #f0f0f0' }}>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Box sx={{ bgcolor: 'white', borderRadius: '50%', width: 40, height: 40,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1,
                      border: '2px solid #8e24aa', boxShadow: '0 0 0 3px #D8D0E8' }}>
                      <People sx={{ color: '#8e24aa', fontSize: 18 }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: friendNudges.length > 0 ? '#8e24aa' : '#bdbdbd' }}>
                      {friendNudges.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>
                      Friend updates
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* === WHY PERCORO HERO (collapsible) === */}
        {whyOpen ? (
          <Box sx={{ border: '1px solid #E0E0E0', borderRadius: '20px', overflow: 'hidden', mb: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, borderBottom: '1px solid #F0F0F0' }}>
              <Typography sx={{ fontWeight: 800, color: '#2D6E6A', fontFamily: 'Poppins, sans-serif' }}>Why Percoro</Typography>
              <Button size="small" onClick={() => { setWhyOpen(false); setShowInlineTour(false); }} sx={{ textTransform: 'none', color: '#555' }}>Collapse &and;</Button>
            </Box>
            <Box sx={{ p: 2.5 }}>
              <WhyPercoroHero />
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button variant="contained" onClick={() => setShowInlineTour(true)}
                  sx={{ bgcolor: '#2D6E6A', borderRadius: '24px', textTransform: 'none', px: 3, fontWeight: 600, '&:hover': { bgcolor: '#245a57' } }}>
                  See Percoro in action
                </Button>
              </Box>
              <Dialog open={showInlineTour} onClose={() => setShowInlineTour(false)}
                maxWidth="sm" fullWidth scroll="paper"
                sx={{ '& .MuiDialog-paper': { borderRadius: '20px' } }}>
                <UserDemoFlow stepIndices={[2, 3, 5, 6, 8]} onFinish={() => setShowInlineTour(false)} />
              </Dialog>
            </Box>
          </Box>
        ) : (
          <Box onClick={() => setWhyOpen(true)}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              bgcolor: '#E8F0EE', border: '1px solid #C8DDD8', borderRadius: '12px',
              px: 2.5, py: 1.5, mb: 3, cursor: 'pointer',
              '&:hover': { bgcolor: '#dceae7' }, transition: 'all 0.2s ease' }}>
            <Typography sx={{ fontWeight: 600, color: '#2D6E6A', fontFamily: 'Poppins, sans-serif' }}>
              Why Percoro? Click here to see what it's about.
            </Typography>
            <Typography sx={{ color: '#2D6E6A', fontSize: '1.3rem', fontWeight: 700, lineHeight: 1 }}>&or;</Typography>
          </Box>
        )}

        {/* === ACTION CARDS === */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontFamily: 'Poppins, sans-serif' }}>
            {newUser ? 'Explore Percoro' : 'Quick actions'}
          </Typography>
          <Grid container spacing={2}>
            {ACTION_CARDS.map((card) => (
              <Grid item xs={6} sm={4} key={card.title}>
                <Card onClick={() => (card as any).proFeature ? (plan === 'free' ? setUpgradeModalOpen(true) : setCompanionInfoOpen(true)) : navigate(card.path)}
                  sx={{ borderRadius: '16px', cursor: 'pointer', boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                    '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.12)', transform: 'translateY(-2px)' },
                    transition: 'all 0.2s ease' }}>
                  <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
                    <Box sx={{ bgcolor: 'white', borderRadius: '50%', width: 52, height: 52,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mx: 'auto', mb: 1.5, color: card.iconColor,
                      border: `2px solid ${card.iconColor}`,
                      boxShadow: `0 0 0 4px ${card.color}` }}>
                      {card.icon}
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25 }}>{card.title}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                      {(card as any).proFeature && plan !== 'free' ? 'Invite companions' : card.subtitle}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* === PLAN UPGRADE MODAL === */}
        <PlanUpgradeModal
          open={upgradeModalOpen}
          onClose={() => setUpgradeModalOpen(false)}
          featureName="Trip Companions"
        />

        {/* === TRIP COMPANIONS INFO MODAL (Pro/Premium users) === */}
        <Dialog open={companionInfoOpen} onClose={() => setCompanionInfoOpen(false)} maxWidth="xs" fullWidth
          PaperProps={{ sx: { borderRadius: '20px', overflow: 'visible' } }}>
          <Box sx={{ bgcolor: '#2D6E6A', borderRadius: '20px 20px 0 0', px: 3, pt: 3, pb: 2.5, position: 'relative' }}>
            <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
              <Button size="small" onClick={() => setCompanionInfoOpen(false)} sx={{ color: 'white', minWidth: 'auto', opacity: 0.8 }}>✕</Button>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '50%', width: 44, height: 44,
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ConnectWithoutContact sx={{ color: 'white', fontSize: '1.4rem' }} />
              </Box>
              <Box>
                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.1rem', fontFamily: 'Poppins, sans-serif' }}>
                  Trip Companions
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>
                  {"You're on the " + plan.charAt(0).toUpperCase() + plan.slice(1) + " Plan"}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, lineHeight: 1.6 }}>
              Share your trips with companions who can add photos, view itineraries, and travel together.
            </Typography>
            {[
              plan === 'premium' ? 'Unlimited trip companions' : 'Up to 6 trip companions per trip',
              'Companions can upload photos for your approval',
              'Set access expiration dates',
              'Get notified before companion access expires',
              'Choose photo keep/delete action on expiry',
            ].map((item, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4 }}>
                <Box sx={{ width: 17, height: 17, borderRadius: '50%', bgcolor: '#2D6E6A', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Typography sx={{ color: 'white', fontSize: '0.65rem', fontWeight: 700 }}>✓</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{item}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ px: 3, pb: 3, pt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button fullWidth variant="contained"
              onClick={() => { setCompanionInfoOpen(false); navigate('/friends'); }}
              sx={{ bgcolor: '#2D6E6A', color: 'white', borderRadius: '24px', textTransform: 'none', fontWeight: 700, py: 1.25,
                '&:hover': { bgcolor: '#1F4E4A' } }}>
              Invite Trip Companions
            </Button>
            <Button fullWidth variant="text" onClick={() => setCompanionInfoOpen(false)}
              sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 500 }}>
              Maybe later
            </Button>
          </Box>
        </Dialog>

        {/* === PLAN STATUS BANNER — hidden for Pro/Premium users === */}
        {plan === 'free' && (
        <Card sx={{ borderRadius: '16px', bgcolor: 'white', border: '1px solid #C8DDD8', boxShadow: 'none', mb: 3 }}>
          <CardContent sx={{ py: 2, px: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ bgcolor: 'white', borderRadius: '50%', width: 40, height: 40, minWidth: 40,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '2px solid #2D6E6A', boxShadow: '0 0 0 3px #C8DDD8' }}>
                <WorkspacePremium sx={{ color: '#2D6E6A', fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  You're on the Free plan
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Plan trips, upload photos, add friends, and explore the community — all free.
                </Typography>
              </Box>
              <Button size="small" variant="outlined"
                onClick={() => navigate('/plans')}
                sx={{ borderColor: '#2D6E6A', color: '#2D6E6A', borderRadius: '20px',
                  textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap',
                  '&:hover': { bgcolor: '#f0faf9' } }}>
                Learn more
              </Button>
            </Box>
          </CardContent>
        </Card>
        )}

        {/* === PLAN STATUS BANNER === */}
        <Card sx={{ borderRadius: '16px', bgcolor: plan === 'free' ? '#f0faf9' : 'white', border: plan === 'free' ? '1px solid #C8DDD8' : '1px solid #2D6E6A', boxShadow: 'none', mb: 3 }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2.5 }}>
            <Box sx={{ bgcolor: 'white', borderRadius: '50%', width: 48, height: 48, minWidth: 48,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '2px solid #2D6E6A', boxShadow: '0 0 0 4px #C8DDD8' }}>
              <WorkspacePremium sx={{ color: '#2D6E6A' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {plan === 'free' ? 'Want more from Percoro?' : "You're on the " + plan.charAt(0).toUpperCase() + plan.slice(1) + " Plan"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {plan === 'free'
                  ? 'Pro is free for a limited time — unlock trip companions, expiration control, and more storage.'
                  : plan === 'pro'
                  ? '15 GB storage, trip companions, expiration control, and more.'
                  : '50 GB storage, unlimited companions, group trips, and more.'}
              </Typography>
            </Box>
            <Button size="small" variant="outlined"
              onClick={() => navigate('/plans')}
              sx={{ borderColor: '#2D6E6A', color: '#2D6E6A', borderRadius: '20px',
                textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap',
                '&:hover': { bgcolor: '#f0faf9' } }}>
              {plan === 'free' ? 'Upgrade' : 'View Plans'}
            </Button>
          </CardContent>
        </Card>
      </Box>
        {/* === INSTALL APP — gated behind 'pwa_install' flag === */}
        {pwaInstallEnabled && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <PwaInstallButton size="medium" />
          </Box>
        )}
    </Box>
  );
};

export default HomePage;